import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Search, X, ChevronLeft, ChevronRight, ChevronDown, FileText, ArrowRight, AlertCircle,
    Save, FlaskConical, Plus, Check, AlertTriangle,
} from 'lucide-react';
import { toast } from 'react-toastify';
import LabServices from '../../shared/ApiServices/LabServices';
import ApiServices from '../../shared/ApiServices/ApiServices';
import { buildPatientDisplayName, formatNameTitleCase } from '../../shared/utils/nameUtils';
import { OrderListSkeleton } from '../../shared/custom-ui/skeleton/Lab';
import { Button, Input, TextArea, Badge, Dialog, Tooltip } from '../../shared/custom-ui';
import {
    extractLabOrdersList, extractLabOrderId, extractPatientName,
    extractOrderStatus,
    extractDoctorsList, extractDoctorName,
    extractLabTestsList, extractLabTestId, extractTestName,
    formatCurrency,
    ORDER_STATUS_META, extractNextToken,
    parseAmount, pickLabColor, getInitials,
    type LabOrder, type ReferenceDoctor, type LabTest,
    extractDoctorId,
    PRIORITY_META, extractOrderPriority, type OrderPriority,
    extractTestGroupsList, extractLabTestGroupId, type LabTestGroup,
    extractAppointmentDate, SLIDE_LIBRARY_STATUS_META, resolveLabStatus,
} from './labUtils';
import { useOrderTemplates } from './useOrderTemplates';

/* ── Modal — thin wrapper over the shared Dialog primitive ─────────────────── */
export const Modal: React.FC<{ title: string; onClose: () => void; children: React.ReactNode; width?: string }> = ({
    title, onClose, children, width = 'max-w-xl',
}) => {
    const isWide = width.includes('3xl') || width.includes('4xl');
    return (
        <Dialog isOpen onClose={onClose} title={title} size={isWide ? 'xl' : 'medium'}
            className={isWide ? 'max-w-3xl' : undefined} preventCloseOnOverlayClick>
            {children}
        </Dialog>
    );
};

/* ── Field — label wrapper for bespoke controls (segmented groups, combobox) ─ */
export const Field: React.FC<{ label: string; required?: boolean; children: React.ReactNode }> = ({ label, required, children }) => (
    <div className="flex flex-col gap-1.5">
        <label className="text-[12px] font-semibold text-[var(--v2-text-secondary)]">
            {label}{required && <span className="text-[var(--v2-danger)] ml-0.5">*</span>}
        </label>
        {children}
    </div>
);

const ReadField: React.FC<{ label: string; value: string; multiline?: boolean }> = ({ label, value, multiline }) => (
    <div className="flex flex-col gap-1">
        <span className="text-[11px] font-semibold text-[var(--v2-text-muted)]">{label}</span>
        <div className={`rounded-lg border border-[var(--v2-border-subtle)] bg-[var(--v2-bg-surface)] px-3 py-2 text-[13px] font-medium text-[var(--v2-text-primary)] ${multiline ? 'min-h-[56px]' : ''}`}>
            {value || <span className="text-[var(--v2-text-muted)] italic">Not provided</span>}
        </div>
    </div>
);

/* ── Compact toolbar filter — status/priority select with a colored dot ────── */
const FilterSelect: React.FC<{
    value: string;
    onChange: (v: string) => void;
    options: { value: string; label: string; dot?: string }[];
    placeholder: string;
}> = ({ value, onChange, options, placeholder }) => {
    const [open, setOpen] = React.useState(false);
    const ref = React.useRef<HTMLDivElement>(null);

    React.useEffect(() => {
        if (!open) return;
        const handler = (e: MouseEvent) => { if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false); };
        document.addEventListener('mousedown', handler);
        return () => document.removeEventListener('mousedown', handler);
    }, [open]);

    React.useEffect(() => {
        if (!open) return;
        const handler = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false); };
        document.addEventListener('keydown', handler);
        return () => document.removeEventListener('keydown', handler);
    }, [open]);

    const selected = options.find(o => o.value === value);
    const label = selected ? selected.label : placeholder;
    const isActive = !!value;

    return (
        <div ref={ref} className="relative">
            <button type="button" onClick={() => setOpen(o => !o)}
                className="flex items-center gap-2 px-3 py-2 rounded-lg text-[12px] font-semibold cursor-pointer border transition-colors"
                style={{
                    background: isActive ? 'var(--v2-primary-50, rgba(4,94,184,0.06))' : 'var(--v2-bg-surface)',
                    borderColor: isActive ? 'var(--v2-primary-400, rgba(4,94,184,0.4))' : 'var(--v2-border-interactive)',
                    color: isActive ? 'var(--v2-primary-600, #045eb8)' : 'var(--v2-text-secondary)',
                }}>
                {selected?.dot && <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ background: selected.dot }} />}
                <span>{label}</span>
                <ChevronDown className="w-3.5 h-3.5 flex-shrink-0 transition-transform" strokeWidth={2.5}
                    style={{ transform: open ? 'rotate(180deg)' : 'rotate(0deg)' }} />
            </button>

            {open && (
                <div className="absolute left-0 top-full mt-1.5 z-[300] rounded-xl border overflow-hidden"
                    style={{ minWidth: '170px', background: 'var(--v2-bg-card)', borderColor: 'var(--v2-border-card)', boxShadow: 'var(--v2-shadow-card)' }}>
                    <div className="p-1.5 flex flex-col gap-0.5">
                        <button type="button" onClick={() => { onChange(''); setOpen(false); }}
                            className="flex items-center gap-2.5 w-full px-2.5 py-2 rounded-lg text-[12px] font-semibold text-left cursor-pointer border-0 transition-colors"
                            style={{ background: !value ? 'var(--v2-bg-hover)' : 'transparent', color: !value ? 'var(--v2-primary-600, #045eb8)' : 'var(--v2-text-secondary)' }}>
                            <span className="w-3.5 h-3.5 flex-shrink-0 flex items-center justify-center">{!value && <Check className="w-3 h-3" strokeWidth={3} />}</span>
                            {placeholder}
                        </button>
                        <div className="h-px mx-2 my-0.5 bg-[var(--v2-border-divider)]" />
                        {options.map(opt => (
                            <button key={opt.value} type="button" onClick={() => { onChange(opt.value); setOpen(false); }}
                                className="flex items-center gap-2.5 w-full px-2.5 py-2 rounded-lg text-[12px] font-semibold text-left cursor-pointer border-0 transition-colors"
                                style={{ background: value === opt.value ? 'var(--v2-bg-hover)' : 'transparent', color: value === opt.value ? 'var(--v2-primary-600, #045eb8)' : 'var(--v2-text-primary)' }}>
                                <span className="w-3.5 h-3.5 flex-shrink-0 flex items-center justify-center">
                                    {opt.dot && <span className="w-2 h-2 rounded-full" style={{ background: opt.dot }} />}
                                    {value === opt.value && !opt.dot && <Check className="w-3 h-3" strokeWidth={3} />}
                                </span>
                                {opt.label}
                            </button>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
};

export const StatusBadge: React.FC<{ status: keyof typeof ORDER_STATUS_META }> = ({ status }) => {
    const meta = ORDER_STATUS_META[status] ?? ORDER_STATUS_META.pending;
    return (
        <Badge shape="pill" size="small" variant="neutral" style={{ background: meta.bg, color: meta.color, border: `1px solid ${meta.color}30` }}>
            {meta.label}
        </Badge>
    );
};

export const PriorityBadge: React.FC<{ priority: OrderPriority }> = ({ priority }) => {
    if (priority === 'routine') return null;
    const meta = PRIORITY_META[priority];
    return (
        <Badge shape="pill" size="small" variant="neutral"
            icon={priority === 'stat' ? <AlertTriangle className="w-full h-full animate-pulse" strokeWidth={3} /> : undefined}
            style={{ background: meta.bg, color: meta.color, border: `1px solid ${meta.color}40` }}>
            {meta.label}
        </Badge>
    );
};

/* ── Small segmented control used for priority + test-view toggles ─────────── */
const Segmented: React.FC<{ options: { value: string; label: string; icon?: React.ReactNode }[]; value: string; onChange: (v: string) => void }> = ({ options, value, onChange }) => (
    <div className="flex gap-1 p-1 bg-[var(--v2-bg-surface)] rounded-lg border border-[var(--v2-border-card)]">
        {options.map(opt => {
            const active = value === opt.value;
            return (
                <button key={opt.value} type="button" onClick={() => onChange(opt.value)}
                    className="flex-1 flex items-center justify-center gap-1.5 py-1.5 px-3 rounded-md text-[12px] font-semibold cursor-pointer transition-colors whitespace-nowrap"
                    style={active
                        ? { background: 'var(--v2-bg-card)', color: 'var(--v2-primary-600, #045eb8)', boxShadow: 'var(--v2-shadow-sm)' }
                        : { background: 'transparent', color: 'var(--v2-text-muted)' }}>
                    {opt.icon}{opt.label}
                </button>
            );
        })}
    </div>
);

interface PlaceOrderModalProps {
    onClose: () => void;
    onSuccess: () => void;
    initialPatient?: any;
}

export const PlaceOrderModal: React.FC<PlaceOrderModalProps> = ({ onClose, onSuccess, initialPatient }) => {
    const navigate = useNavigate();
    const [step, setStep] = useState(1);
    const [saving, setSaving] = useState(false);
    const [doctors, setDoctors] = useState<ReferenceDoctor[]>([]);
    const [allTests, setAllTests] = useState<LabTest[]>([]);
    const [testGroups, setTestGroups] = useState<LabTestGroup[]>([]);
    const [testSearch, setTestSearch] = useState('');
    const [testViewMode, setTestViewMode] = useState<'individual' | 'panels'>('individual');
    const [selectedTests, setSelectedTests] = useState<{ testId: string; testName: string; price: number; qty: number; discountPct: number }[]>([]);
    const { templates, saveTemplate, deleteTemplate } = useOrderTemplates();
    const [showSaveTemplate, setShowSaveTemplate] = useState(false);
    const [templateName, setTemplateName] = useState('');
    const [form, setForm] = useState({
        patientId: '', patientName: '', mobile: '', email: '', age: '', gender: 'Male',
        address: '', referenceDoctor: '', paidAmount: '', paymentMode: 'Cash', comments: '',
        discountAmount: '0', priority: 'routine' as OrderPriority,
    });

    const [patientLocked, setPatientLocked] = useState(!!initialPatient);

    // ── Patient live-search state ────────────────────────────────────────────
    const [patientQuery, setPatientQuery] = useState('');
    const [patientResults, setPatientResults] = useState<any[]>([]);
    const [patientLoading, setPatientLoading] = useState(false);
    const [showPatientDrop, setShowPatientDrop] = useState(false);
    const patientSearchRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    const searchPatients = (q: string) => {
        setPatientQuery(q);
        setShowPatientDrop(true);
        if (patientSearchRef.current) clearTimeout(patientSearchRef.current);
        patientSearchRef.current = setTimeout(async () => {
            setPatientLoading(true);
            try {
                const res = await ApiServices.getAllPatients({ patientName: q || undefined, limit: 20 });
                setPatientResults(Array.isArray(res?.items) ? res.items : []);
            } catch { setPatientResults([]); } finally { setPatientLoading(false); }
        }, 350);
    };

    const selectPatient = (p: any) => {
        const displayName = buildPatientDisplayName(p) || p.PatientName || p.name || '';
        setPatientQuery(displayName);
        setShowPatientDrop(false);
        const birthDate = p.BirthDate ?? p.birthDate ?? p.DOB ?? p.dob;
        const computedAge = birthDate ? Math.max(0, Math.floor((Date.now() - new Date(birthDate).getTime()) / 31557600000)) : undefined;
        const rawPk: string = p.PK ?? '';
        const patientId = rawPk.startsWith('PATIENT#') ? rawPk.replace('PATIENT#', '') : rawPk;
        setForm(f => ({
            ...f,
            patientId,
            patientName: displayName,
            mobile: p.Contact ?? p.contact ?? p.mobile ?? p.Mobile ?? p.phone ?? p.Phone ?? f.mobile,
            email: p.email ?? p.Email ?? f.email,
            age: String(p.age ?? p.Age ?? computedAge ?? f.age),
            gender: p.gender ?? p.Gender ?? p.sex ?? p.Sex ?? f.gender,
            address: p.address ?? p.Address ?? f.address,
        }));
        setPatientLocked(true);
    };

    useEffect(() => {
        ApiServices.getAllPatients({ limit: 20 })
            .then(res => setPatientResults(Array.isArray(res?.items) ? res.items : []))
            .catch(() => { });
    }, []);

    useEffect(() => {
        if (initialPatient) selectPatient(initialPatient);
    }, []);

    useEffect(() => {
        Promise.all([LabServices.listReferenceDoctors(), LabServices.getLabTests(), LabServices.getTestGroups()])
            .then(([d, t, g]) => { setDoctors(extractDoctorsList(d)); setAllTests(extractLabTestsList(t)); setTestGroups(extractTestGroupsList(g)); })
            .catch(() => { });
    }, []);

    const filteredTests = allTests.filter(t =>
        testSearch === '' || (extractTestName(t).toLowerCase().includes(testSearch.toLowerCase()) ||
            (t.testCode ?? t.test_code ?? '').toLowerCase().includes(testSearch.toLowerCase()))
    );

    const toggleTest = (t: LabTest) => {
        const id = extractLabTestId(t);
        if (selectedTests.find(s => s.testId === id)) {
            setSelectedTests(prev => prev.filter(s => s.testId !== id));
        } else {
            setSelectedTests(prev => [...prev, { testId: id, testName: extractTestName(t), price: parseAmount(t.price), qty: 1, discountPct: 0 }]);
        }
    };

    const applyPanel = (group: LabTestGroup) => {
        const memberIds = group.testIds ?? group.tests ?? [];
        const toAdd = memberIds
            .map(id => allTests.find(t => extractLabTestId(t) === id))
            .filter((t): t is LabTest => Boolean(t))
            .filter(t => !selectedTests.find(s => s.testId === extractLabTestId(t)));
        setSelectedTests(prev => [
            ...prev,
            ...toAdd.map(t => ({ testId: extractLabTestId(t), testName: extractTestName(t), price: parseAmount(t.price), qty: 1, discountPct: 0 })),
        ]);
    };

    const applyTemplate = (tmpl: ReturnType<typeof useOrderTemplates>['templates'][number]) => {
        setSelectedTests(tmpl.tests.map(t => ({ testId: t.testId, testName: t.testName, price: t.price, qty: 1, discountPct: 0 })));
        if (tmpl.referenceDoctor) setForm(f => ({ ...f, referenceDoctor: tmpl.referenceDoctor! }));
    };

    const handleSaveTemplate = () => {
        if (!templateName.trim() || selectedTests.length === 0) return;
        saveTemplate(
            templateName.trim(),
            selectedTests.map(t => ({ testId: t.testId, testName: t.testName, price: t.price })),
            form.referenceDoctor || undefined
        );
        toast.success('Order template saved');
        setTemplateName('');
        setShowSaveTemplate(false);
    };

    const subtotal = selectedTests.reduce((a, t) => a + (t.price * t.qty), 0);
    const gstRate = 0.12;
    const discount = Number(form.discountAmount) || 0;
    const afterDiscount = Math.max(0, subtotal - discount);
    const gstAmount = afterDiscount * gstRate;
    const total = afterDiscount + gstAmount;
    const paid = Number(form.paidAmount) || 0;
    const balance = Math.max(0, total - paid);

    const handleSubmit = async () => {
        if (!form.patientName.trim()) { toast.error('Patient name is required'); return; }
        if (!form.patientId.trim()) { toast.error('Patient ID is required'); return; }
        if (selectedTests.length === 0) { toast.error('Select at least one test'); return; }
        setSaving(true);
        try {
            const res: any = await LabServices.placeOrder({
                patientId: form.patientId.trim(),
                patientName: form.patientName.trim(),
                mobile: form.mobile || undefined,
                email: form.email || undefined,
                age: Number(form.age) || undefined,
                gender: form.gender,
                address: form.address || undefined,
                referenceDoctor: form.referenceDoctor || undefined,
                discountAmount: discount,
                paidAmount: paid,
                paymentMode: form.paymentMode,
                comments: form.comments || undefined,
                priority: form.priority,
                tests: selectedTests.map(t => ({ testId: t.testId, quantity: t.qty, discountPct: t.discountPct })),
            });
            toast.success(res?.message ?? 'Order placed successfully');

            const createdOrderId = res?.orderId || res?.order_id || res?.LabOrderId || res?.id || (res?.PK ? res.PK.replace('ORDER#', '') : null);
            if (createdOrderId) {
                navigate(`/lab/orders/order-details?orderId=${encodeURIComponent(createdOrderId)}`);
            }
            onSuccess(); onClose();
        } catch (err: any) {
            toast.error(err?.response?.data?.message ?? err?.message ?? 'Failed to place order');
        } finally { setSaving(false); }
    };

    return (
        <Modal title="Place New Lab Order" onClose={onClose} width="max-w-3xl">
            {/* Step indicator */}
            <div className="flex items-center gap-2 mb-6">
                {[1, 2, 3].map((s, i) => {
                    const isActive = step >= s;
                    const stepLabel = s === 1 ? 'Patient' : s === 2 ? 'Tests' : 'Payment';
                    return (
                        <React.Fragment key={s}>
                            <div className="flex items-center gap-2 flex-shrink-0">
                                <div className="w-6 h-6 rounded-full flex items-center justify-center text-[11px] font-bold flex-shrink-0"
                                    style={isActive
                                        ? { background: 'var(--v2-primary-500)', color: '#fff' }
                                        : { background: 'var(--v2-bg-surface)', color: 'var(--v2-text-muted)', border: '1px solid var(--v2-border-card)' }}>
                                    {step > s ? <Check className="w-3 h-3" strokeWidth={3} /> : s}
                                </div>
                                <span className="text-[12px] font-semibold whitespace-nowrap" style={{ color: isActive ? 'var(--v2-text-heading)' : 'var(--v2-text-muted)' }}>{stepLabel}</span>
                            </div>
                            {i < 2 && <div className="flex-1 h-px" style={{ background: step > s ? 'var(--v2-primary-500)' : 'var(--v2-border-subtle)' }} />}
                        </React.Fragment>
                    );
                })}
            </div>

            <div className="min-h-[360px]">
            {step === 1 && (
                <div className="flex flex-col gap-4">
                    <Field label="Search Patient" required>
                        <div className="relative">
                            <Input
                                leftIcon={<Search className="w-full h-full" strokeWidth={2} />}
                                placeholder="Type name or MRN to search…"
                                value={patientQuery}
                                readOnly={patientLocked}
                                onChange={e => { if (!patientLocked) searchPatients(e.target.value); }}
                                onFocus={() => { if (!patientLocked) setShowPatientDrop(true); }}
                                autoComplete={false}
                                className={patientLocked ? 'cursor-not-allowed opacity-80' : ''}
                            />
                            {patientLocked && !initialPatient && (
                                <button type="button"
                                    onClick={() => {
                                        setPatientLocked(false);
                                        setPatientQuery('');
                                        setForm(f => ({ ...f, patientId: '', patientName: '', mobile: '', email: '', age: '', gender: 'Male', address: '' }));
                                        setShowPatientDrop(true);
                                    }}
                                    className="absolute right-3 top-1/2 -translate-y-1/2 text-[11px] font-bold px-2.5 py-1 rounded-full border-0 cursor-pointer transition-colors"
                                    style={{ background: 'var(--v2-primary-50, rgba(4,94,184,0.08))', color: 'var(--v2-primary-600, #045eb8)' }}>
                                    Change
                                </button>
                            )}
                            {!patientLocked && showPatientDrop && (
                                <div className="absolute z-50 left-0 right-0 top-full mt-1.5 rounded-xl border border-[var(--v2-border-card)] bg-[var(--v2-bg-card)] shadow-[var(--v2-shadow-card)] overflow-hidden max-h-60 overflow-y-auto v2-scrollbar">
                                    {patientLoading && (
                                        <div className="px-4 py-3 text-[12px] text-[var(--v2-text-muted)] flex items-center gap-2">
                                            <div className="w-3 h-3 rounded-full border-2 border-[var(--v2-text-muted)] border-t-transparent animate-spin" /> Searching…
                                        </div>
                                    )}
                                    {!patientLoading && patientResults.length === 0 && (
                                        <div className="px-4 py-3 text-[12px] text-[var(--v2-text-muted)]">No patients found</div>
                                    )}
                                    {patientResults.map((p: any) => {
                                        const name = buildPatientDisplayName(p) || p.PatientName || p.name || p.PK;
                                        const mrn = p.mrn ?? p.MRN ?? '';
                                        return (
                                            <button key={p.PK} type="button"
                                                className="w-full text-left px-4 py-2.5 hover:bg-[var(--v2-bg-hover)] border-b border-[var(--v2-border-divider)] last:border-0 flex items-center gap-3 cursor-pointer bg-transparent transition-colors"
                                                onMouseDown={() => selectPatient(p)}>
                                                <div className="w-8 h-8 rounded-full flex-shrink-0 flex items-center justify-center text-[11px] font-bold text-white" style={{ background: pickLabColor(p.PK ?? name) }}>
                                                    {name.charAt(0).toUpperCase()}
                                                </div>
                                                <div className="flex flex-col min-w-0">
                                                    <span className="text-[13px] font-semibold text-[var(--v2-text-primary)] truncate">{name}</span>
                                                    <div className="flex items-center gap-2 text-[10px] text-[var(--v2-text-muted)]">
                                                        {mrn && <span className="font-medium bg-[var(--v2-bg-surface)] px-1.5 py-0.5 rounded">MRN: {mrn}</span>}
                                                        <span className="font-mono truncate opacity-70">{p.PK}</span>
                                                    </div>
                                                </div>
                                            </button>
                                        );
                                    })}
                                </div>
                            )}
                        </div>
                    </Field>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 bg-[var(--v2-bg-surface)] p-3 rounded-xl border border-[var(--v2-border-card)]">
                        {patientLocked ? (
                            <>
                                <ReadField label="Mobile" value={form.mobile} />
                                <ReadField label="Email" value={form.email} />
                                <ReadField label="Age" value={form.age} />
                                <ReadField label="Gender" value={form.gender} />
                                <div className="sm:col-span-2"><ReadField label="Address" value={form.address} multiline /></div>
                            </>
                        ) : (
                            <>
                                <Input label="Mobile" value={form.mobile} onChange={e => setForm(f => ({ ...f, mobile: e.target.value }))} placeholder="+91 98765 43210" />
                                <Input label="Email" type="email" value={form.email} onChange={e => setForm(f => ({ ...f, email: e.target.value }))} placeholder="patient@example.com" />
                                <Input label="Age" type="number" value={form.age} onChange={e => setForm(f => ({ ...f, age: e.target.value }))} placeholder="Years" />
                                <Field label="Gender">
                                    <select className="w-full rounded-[var(--v2-radius-md)] border border-[var(--v2-border-glass-strong)] bg-[var(--v2-bg-card)] px-3.5 py-2.5 text-[15px] text-[var(--v2-text-primary)] outline-none focus:ring-[3px] focus:ring-[var(--v2-primary-500)]/18 focus:border-[var(--v2-primary-500)]"
                                        value={form.gender} onChange={e => setForm(f => ({ ...f, gender: e.target.value }))}>
                                        {['Male', 'Female', 'Other'].map(g => <option key={g}>{g}</option>)}
                                    </select>
                                </Field>
                                <div className="sm:col-span-2"><TextArea label="Address" rows={2} value={form.address} onChange={e => setForm(f => ({ ...f, address: e.target.value }))} placeholder="Full address" /></div>
                            </>
                        )}
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        <Field label="Reference Doctor">
                            <select className="w-full rounded-[var(--v2-radius-md)] border border-[var(--v2-border-glass-strong)] bg-[var(--v2-bg-card)] px-3.5 py-2.5 text-[15px] text-[var(--v2-text-primary)] outline-none focus:ring-[3px] focus:ring-[var(--v2-primary-500)]/18 focus:border-[var(--v2-primary-500)]"
                                value={form.referenceDoctor} onChange={e => setForm(f => ({ ...f, referenceDoctor: e.target.value }))}>
                                <option value="">— Self / None —</option>
                                {doctors.map(d => <option key={extractDoctorId(d)} value={extractDoctorName(d)}>{extractDoctorName(d)}</option>)}
                            </select>
                        </Field>
                        <Field label="Priority">
                            <Segmented value={form.priority} onChange={v => setForm(f => ({ ...f, priority: v as OrderPriority }))}
                                options={(['routine', 'urgent', 'stat'] as OrderPriority[]).map(p => ({
                                    value: p, label: PRIORITY_META[p].label,
                                    icon: p === 'stat' ? <AlertTriangle className="w-3.5 h-3.5" strokeWidth={2.5} /> : undefined,
                                }))} />
                        </Field>
                    </div>

                    <div className="flex justify-end mt-1">
                        <Button variant="primary" onClick={() => setStep(2)} rightIcon={<ArrowRight className="w-full h-full" strokeWidth={2} />}>
                            Continue to Tests
                        </Button>
                    </div>
                </div>
            )}

            {/* Step 2 — Tests */}
            {step === 2 && (
                <div className="flex flex-col gap-3 h-full">
                    {templates.length > 0 && (
                        <div className="flex items-center gap-2 flex-wrap bg-[var(--v2-bg-surface)] p-2 rounded-lg border border-[var(--v2-border-card)]">
                            <span className="text-[11px] font-semibold text-[var(--v2-text-secondary)] pl-2 flex-shrink-0">Quick Templates:</span>
                            {templates.map(tmpl => (
                                <span key={tmpl.id} className="inline-flex items-center gap-1 pl-2.5 pr-1 py-1 rounded-full text-[12px] font-semibold"
                                    style={{ background: 'var(--v2-bg-card)', color: '#7C3AED', border: '1px solid rgba(124,58,237,0.3)' }}>
                                    <button type="button" onClick={() => applyTemplate(tmpl)} className="cursor-pointer border-0 bg-transparent" style={{ color: 'inherit' }}>{tmpl.name}</button>
                                    <button type="button" onClick={() => deleteTemplate(tmpl.id)} title="Delete template"
                                        className="w-4 h-4 flex items-center justify-center rounded-full cursor-pointer border-0 bg-transparent hover:bg-red-50 hover:text-red-600 transition-colors">
                                        <X className="w-3 h-3" strokeWidth={2.5} />
                                    </button>
                                </span>
                            ))}
                        </div>
                    )}

                    <div className="w-fit">
                        <Segmented value={testViewMode} onChange={v => setTestViewMode(v as 'individual' | 'panels')}
                            options={[{ value: 'individual', label: 'Individual Tests' }, { value: 'panels', label: 'Panels (Order Sets)' }]} />
                    </div>

                    <div className="flex gap-3 min-h-[300px]">
                        {/* Test Catalog */}
                        <div className="flex-[3] flex flex-col gap-2">
                            {testViewMode === 'individual' ? (
                                <>
                                    <Input leftIcon={<Search className="w-full h-full" strokeWidth={2} />} placeholder="Search tests by name or code…"
                                        value={testSearch} onChange={e => setTestSearch(e.target.value)} size="small" />
                                    <div className="rounded-xl border border-[var(--v2-border-card)] bg-[var(--v2-bg-card)] max-h-[350px] overflow-y-auto v2-scrollbar">
                                        {filteredTests.map(t => {
                                            const id = extractLabTestId(t);
                                            const selected = selectedTests.find(s => s.testId === id);
                                            return (
                                                <div key={id} onClick={() => toggleTest(t)}
                                                    className="flex items-center justify-between px-3.5 py-2.5 cursor-pointer hover:bg-[var(--v2-bg-hover)] transition-colors border-b border-[var(--v2-border-divider)] last:border-0">
                                                    <div className="flex items-center gap-3">
                                                        <div className={`w-4.5 h-4.5 rounded flex items-center justify-center flex-shrink-0 transition-colors ${selected ? '' : 'border-2 border-[var(--v2-border-interactive)] bg-transparent'}`}
                                                            style={selected ? { background: 'var(--v2-primary-500)' } : undefined}>
                                                            {selected && <Check className="w-3 h-3 text-white" strokeWidth={3} />}
                                                        </div>
                                                        <div>
                                                            <p className="text-[13px] font-semibold text-[var(--v2-text-primary)] m-0">{extractTestName(t)}</p>
                                                            <p className="text-[11px] text-[var(--v2-text-muted)] m-0 mt-0.5"><span className="bg-[var(--v2-bg-surface)] px-1.5 rounded">{t.category ?? 'General'}</span> {t.testCode ?? t.test_code ?? ''}</p>
                                                        </div>
                                                    </div>
                                                    <span className="text-[13px] font-bold text-[var(--v2-text-secondary)]">{formatCurrency(t.price)}</span>
                                                </div>
                                            );
                                        })}
                                    </div>
                                </>
                            ) : (
                                <div className="rounded-xl border border-[var(--v2-border-card)] bg-[var(--v2-bg-card)] max-h-[350px] overflow-y-auto v2-scrollbar">
                                    {testGroups.length === 0 && (
                                        <p className="px-4 py-8 text-[13px] text-[var(--v2-text-muted)] text-center font-medium">No panels configured in the catalog.</p>
                                    )}
                                    {testGroups.map(g => {
                                        const memberIds = g.testIds ?? g.tests ?? [];
                                        const allSelected = memberIds.length > 0 && memberIds.every(id => selectedTests.find(s => s.testId === id));
                                        return (
                                            <div key={extractLabTestGroupId(g)} onClick={() => applyPanel(g)}
                                                className="flex items-center justify-between px-3.5 py-2.5 cursor-pointer hover:bg-[var(--v2-bg-hover)] transition-colors border-b border-[var(--v2-border-divider)] last:border-0 group">
                                                <div>
                                                    <p className="text-[13px] font-semibold text-[var(--v2-text-primary)] m-0">{g.groupName ?? (g as any).group_name}</p>
                                                    <p className="text-[11px] text-[var(--v2-text-muted)] m-0 mt-0.5">{memberIds.length} test{memberIds.length !== 1 ? 's' : ''}{g.description ? ` · ${g.description}` : ''}</p>
                                                </div>
                                                {allSelected
                                                    ? <span className="text-[11px] font-bold px-2.5 py-1 rounded-full flex-shrink-0" style={{ background: 'rgba(5,150,105,0.1)', color: '#059669' }}>Added</span>
                                                    : <span className="text-[11px] font-semibold px-2.5 py-1 rounded-full flex-shrink-0 opacity-0 group-hover:opacity-100 transition-opacity" style={{ color: 'var(--v2-primary-600, #045eb8)', background: 'var(--v2-primary-50, rgba(4,94,184,0.08))' }}>+ Add Panel</span>
                                                }
                                            </div>
                                        );
                                    })}
                                </div>
                            )}
                        </div>

                        {/* Cart */}
                        <div className="flex-[2] flex flex-col bg-[var(--v2-bg-surface)] rounded-xl border border-[var(--v2-border-card)] overflow-hidden h-[400px]">
                            <div className="bg-[var(--v2-bg-card)] px-3.5 py-2.5 border-b border-[var(--v2-border-divider)] flex items-center justify-between">
                                <p className="text-[11px] font-bold text-[var(--v2-text-heading)] m-0 uppercase tracking-wide flex items-center gap-1.5">
                                    <FlaskConical className="w-3.5 h-3.5" style={{ color: 'var(--v2-primary-500)' }} /> Order Cart
                                </p>
                                <span className="text-white text-[10px] font-bold px-1.5 py-0.5 rounded-full" style={{ background: 'var(--v2-primary-500)' }}>{selectedTests.length}</span>
                            </div>

                            <div className="flex-1 overflow-y-auto v2-scrollbar p-2.5 flex flex-col gap-2">
                                {selectedTests.length === 0 ? (
                                    <div className="flex flex-col items-center justify-center h-full opacity-50">
                                        <AlertCircle className="w-7 h-7 mb-2" strokeWidth={1.5} />
                                        <span className="text-[12px] font-semibold">No tests selected</span>
                                    </div>
                                ) : (
                                    selectedTests.map(s => (
                                        <div key={s.testId} className="flex items-center gap-2 bg-[var(--v2-bg-card)] p-2 rounded-lg border border-[var(--v2-border-subtle)]">
                                            <div className="flex-1 min-w-0">
                                                <p className="text-[12px] font-semibold text-[var(--v2-text-primary)] truncate m-0">{s.testName}</p>
                                                <p className="text-[11px] font-bold m-0" style={{ color: 'var(--v2-primary-600, #045eb8)' }}>{formatCurrency(s.price)}</p>
                                            </div>
                                            <button type="button" onClick={() => setSelectedTests(p => p.filter(x => x.testId !== s.testId))}
                                                className="w-6 h-6 rounded-md flex items-center justify-center cursor-pointer text-red-400 hover:bg-red-50 hover:text-red-600 bg-transparent border-0 transition-colors">
                                                <X className="w-3.5 h-3.5" strokeWidth={2.5} />
                                            </button>
                                        </div>
                                    ))
                                )}
                            </div>

                            {selectedTests.length > 0 && (
                                <div className="p-2.5 bg-[var(--v2-bg-card)] border-t border-[var(--v2-border-divider)]">
                                    <div className="flex items-center justify-between mb-2.5">
                                        <span className="text-[12px] font-semibold text-[var(--v2-text-muted)]">Subtotal:</span>
                                        <span className="text-[14px] font-bold text-[var(--v2-text-heading)]">{formatCurrency(subtotal)}</span>
                                    </div>
                                    {!showSaveTemplate ? (
                                        <button type="button" onClick={() => setShowSaveTemplate(true)}
                                            className="w-full py-1.5 text-[11px] font-bold text-[#7C3AED] hover:bg-[#7C3AED]/10 rounded-lg transition-colors border-0 bg-transparent cursor-pointer">
                                            + Save as Quick Template
                                        </button>
                                    ) : (
                                        <div className="flex items-center gap-1.5">
                                            <input className="flex-1 rounded-lg border border-[var(--v2-border-interactive)] bg-[var(--v2-bg-surface)] py-1.5 px-3 text-[11px] outline-none focus:border-[var(--v2-primary-500)]"
                                                placeholder="Template name…" value={templateName}
                                                onChange={e => setTemplateName(e.target.value)} onKeyDown={e => e.key === 'Enter' && handleSaveTemplate()} />
                                            <button type="button" onClick={handleSaveTemplate} disabled={!templateName.trim()}
                                                className="px-3 py-1.5 rounded-lg text-[11px] font-bold cursor-pointer text-white border-0 disabled:opacity-50 flex-shrink-0" style={{ background: '#7C3AED' }}>
                                                Save
                                            </button>
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                    </div>

                    <div className="flex gap-3 justify-between mt-1 pt-3 border-t border-[var(--v2-border-subtle)]">
                        <Button variant="outline" onClick={() => setStep(1)}>Back</Button>
                        <Button variant="primary" onClick={() => setStep(3)} disabled={selectedTests.length === 0} rightIcon={<ArrowRight className="w-full h-full" strokeWidth={2} />}>
                            Review &amp; Payment
                        </Button>
                    </div>
                </div>
            )}

            {/* Step 3 — Payment */}
            {step === 3 && (
                <div className="flex flex-col gap-5">
                    <div className="rounded-xl border border-[var(--v2-border-card)] bg-[var(--v2-bg-surface)] p-4">
                        <div className="flex items-center gap-2 mb-3">
                            <FileText className="w-4 h-4" style={{ color: 'var(--v2-primary-500)' }} />
                            <h3 className="text-[13px] font-bold text-[var(--v2-text-heading)] m-0">Billing Summary</h3>
                        </div>
                        <div className="flex flex-col gap-2">
                            {[
                                { label: 'Subtotal', value: formatCurrency(subtotal) },
                                { label: 'Discount', value: `- ${formatCurrency(discount)}`, color: '#059669' },
                                { label: 'GST (12%)', value: formatCurrency(gstAmount) },
                            ].map(({ label, value, color }) => (
                                <div key={label} className="flex items-center justify-between text-[13px]">
                                    <span className="text-[var(--v2-text-secondary)] font-medium">{label}</span>
                                    <span className="font-bold" style={{ color: color ?? 'var(--v2-text-primary)' }}>{value}</span>
                                </div>
                            ))}
                            <div className="h-px bg-[var(--v2-border-divider)] my-1.5" />
                            <div className="flex items-center justify-between">
                                <span className="text-[15px] font-bold text-[var(--v2-text-heading)]">Total Amount</span>
                                <span className="text-[19px] font-black" style={{ color: 'var(--v2-primary-600, #045eb8)' }}>{formatCurrency(total)}</span>
                            </div>
                            <div className="flex items-center justify-between text-[13px] mt-1 bg-[var(--v2-bg-card)] px-3 py-2 rounded-lg border border-[var(--v2-border-subtle)]">
                                <span className="text-[var(--v2-text-secondary)] font-bold">Balance Due</span>
                                <span className="font-black text-[14px]" style={{ color: balance > 0 ? '#DC2626' : '#059669' }}>{formatCurrency(balance)}</span>
                            </div>
                        </div>
                    </div>

                    <div className="grid grid-cols-2 gap-3">
                        <Input label="Discount Amount (₹)" type="number" value={form.discountAmount} onChange={e => setForm(f => ({ ...f, discountAmount: e.target.value }))} />
                        <Input label="Amount Paid (₹)" type="number" value={form.paidAmount} onChange={e => setForm(f => ({ ...f, paidAmount: e.target.value }))} />
                    </div>

                    <div className="grid grid-cols-2 gap-3">
                        <Field label="Payment Mode">
                            <select className="w-full rounded-[var(--v2-radius-md)] border border-[var(--v2-border-glass-strong)] bg-[var(--v2-bg-card)] px-3.5 py-2.5 text-[15px] text-[var(--v2-text-primary)] outline-none focus:ring-[3px] focus:ring-[var(--v2-primary-500)]/18 focus:border-[var(--v2-primary-500)]"
                                value={form.paymentMode} onChange={e => setForm(f => ({ ...f, paymentMode: e.target.value }))}>
                                {['Cash', 'Card', 'UPI', 'Online', 'Insurance', 'Other'].map(m => <option key={m}>{m}</option>)}
                            </select>
                        </Field>
                        <Input label="Comments" value={form.comments} onChange={e => setForm(f => ({ ...f, comments: e.target.value }))} placeholder="Any notes..." />
                    </div>

                    <div className="flex gap-3 justify-between mt-1 pt-3 border-t border-[var(--v2-border-subtle)]">
                        <Button variant="outline" onClick={() => setStep(2)}>Back</Button>
                        <Button variant="primary" onClick={handleSubmit} loading={saving} leftIcon={!saving ? <Save className="w-full h-full" strokeWidth={2.5} /> : undefined}>
                            {saving ? 'Placing Order…' : 'Confirm & Place Order'}
                        </Button>
                    </div>
                </div>
            )}
            </div>
        </Modal>
    );
};

export const RecordPaymentModal: React.FC<{ orderId: string; balance: number; onClose: () => void; onSuccess: () => void }> = ({ orderId, balance, onClose, onSuccess }) => {
    const [form, setForm] = useState({ amount: String(balance), paymentMode: 'Cash', transactionId: '', comments: '' });
    const [saving, setSaving] = useState(false);
    const [result, setResult] = useState<{ paidAmount: number; balanceAmount: number } | null>(null);

    const handleSave = async () => {
        if (!form.amount || Number(form.amount) <= 0) { toast.error('Enter a valid amount'); return; }
        setSaving(true);
        try {
            const res = await LabServices.recordPayment(orderId, {
                amount: Number(form.amount),
                paymentMode: form.paymentMode,
                transactionId: form.transactionId || undefined,
                comments: form.comments || undefined,
            });
            setResult({ paidAmount: res?.paidAmount ?? 0, balanceAmount: res?.balanceAmount ?? 0 });
            toast.success(res?.message ?? 'Payment recorded');
            onSuccess();
        } catch (err: any) { toast.error(err?.message ?? 'Failed'); } finally { setSaving(false); }
    };

    return (
        <Modal title="Record Payment" onClose={onClose}>
            <div className="flex flex-col gap-3">
                {result ? (
                    <div className="flex flex-col gap-3">
                        <div className="rounded-xl p-4 text-center flex flex-col gap-2" style={{ background: 'rgba(5,150,105,0.08)', border: '1px solid rgba(5,150,105,0.2)' }}>
                            <p className="text-[11px] font-bold uppercase tracking-wide text-[#059669] m-0">Payment Recorded</p>
                            <p className="text-[22px] font-black text-[#059669] m-0">{formatCurrency(result.paidAmount)}</p>
                            <p className="text-[11px] text-[var(--v2-text-muted)] m-0">
                                New Balance: <strong style={{ color: result.balanceAmount > 0 ? '#DC2626' : '#059669' }}>{formatCurrency(result.balanceAmount)}</strong>
                            </p>
                        </div>
                        <Button variant="primary" fullWidth onClick={onClose}>Done</Button>
                    </div>
                ) : (
                    <>
                        <div className="rounded-lg px-3 py-2.5 text-[12px] font-semibold" style={{ background: 'rgba(239,68,68,0.07)', color: '#DC2626' }}>
                            Balance Due: <strong>{formatCurrency(balance)}</strong>
                        </div>
                        <Input label="Amount (₹)" required type="number" value={form.amount} onChange={e => setForm(f => ({ ...f, amount: e.target.value }))} />
                        <Field label="Payment Mode">
                            <select className="w-full rounded-[var(--v2-radius-md)] border border-[var(--v2-border-glass-strong)] bg-[var(--v2-bg-card)] px-3.5 py-2.5 text-[15px] text-[var(--v2-text-primary)] outline-none focus:ring-[3px] focus:ring-[var(--v2-primary-500)]/18 focus:border-[var(--v2-primary-500)]"
                                value={form.paymentMode} onChange={e => setForm(f => ({ ...f, paymentMode: e.target.value }))}>
                                {['Cash', 'Card', 'UPI', 'Online', 'Insurance', 'Other'].map(m => <option key={m}>{m}</option>)}
                            </select>
                        </Field>
                        <Input label="Transaction ID" value={form.transactionId} onChange={e => setForm(f => ({ ...f, transactionId: e.target.value }))} />
                        <TextArea label="Comments" rows={2} value={form.comments} onChange={e => setForm(f => ({ ...f, comments: e.target.value }))} />
                        <div className="flex gap-2 justify-end mt-1">
                            <Button variant="outline" onClick={onClose}>Cancel</Button>
                            <Button variant="primary" onClick={handleSave} loading={saving}>Record Payment</Button>
                        </div>
                    </>
                )}
            </div>
        </Modal>
    );
};

export const OrderManagement: React.FC = () => {
    const [orders, setOrders] = useState<LabOrder[]>([]);
    const [loading, setLoading] = useState(true);
    const [invoiceSearch, setInvoiceSearch] = useState('');
    const [mobileSearch, setMobileSearch] = useState('');
    const [statusFilter, setStatusFilter] = useState('');
    const [priorityFilter, setPriorityFilter] = useState<OrderPriority | ''>('');
    const [tokenStack, setTokenStack] = useState<string[]>(['']);
    const [pageIdx, setPageIdx] = useState(0);
    const [showCreate, setShowCreate] = useState(false);
    const [viewingReportId, setViewingReportId] = useState<string | null>(null);
    const mountedRef = useRef(true);
    const navigate = useNavigate();

    const openOrder = (order: LabOrder) => {
        navigate(`/lab/orders/order-details?orderId=${encodeURIComponent(extractLabOrderId(order))}`);
    };

    // Report is already generated (reportKey/reportGeneratedAt present, surfaced via resolveLabStatus
    // as REPORT_READY) — fetch the presigned PDF URL and open it, generating on-demand as a fallback
    // for orders that resolved to "ready" purely from orderStatus === 'completed'.
    const handleViewReport = async (order: LabOrder, e: React.MouseEvent) => {
        e.stopPropagation();
        const id = extractLabOrderId(order);
        const isValidUrl = (u: string) => u.startsWith('http://') || u.startsWith('https://');
        setViewingReportId(id);
        try {
            const res: any = await LabServices.getReportPdfUrl(id);
            const url: string = res?.reportUrl ?? res?.report_url ?? res?.url ?? res?.presignedUrl ?? '';
            if (url && isValidUrl(url)) { window.open(url, '_blank', 'noopener,noreferrer'); return; }

            const genRes: any = await LabServices.generateReportPdf(id);
            const getRes: any = await LabServices.getReportPdfUrl(id).catch(() => null);
            const openUrl = getRes?.reportUrl ?? getRes?.report_url ?? getRes?.url ?? getRes?.presignedUrl ?? '';
            if (openUrl && isValidUrl(openUrl)) { window.open(openUrl, '_blank', 'noopener,noreferrer'); }
            else { toast.info(genRes?.message ?? 'Report is being generated — try again shortly.'); }
        } catch (err: any) {
            toast.error(err?.message ?? 'Failed to open report');
        } finally {
            setViewingReportId(null);
        }
    };

    const loadOrders = useCallback(async (token = '') => {
        setLoading(true);
        try {
            const res = await LabServices.listOrders({
                status: statusFilter || undefined,
                mobile: mobileSearch || undefined,
                next_token: token || undefined,
                limit: 25,
            });
            const list = extractLabOrdersList(res);
            if (mountedRef.current) setOrders(list);
            const next = extractNextToken(res);
            if (next && !tokenStack.includes(next)) setTokenStack(prev => [...prev.slice(0, pageIdx + 1), next]);
        } catch (err: any) { toast.error(err?.message ?? 'Failed to load orders'); }
        finally { if (mountedRef.current) setLoading(false); }
    }, [statusFilter, mobileSearch, pageIdx, tokenStack]);

    useEffect(() => { mountedRef.current = true; loadOrders(tokenStack[pageIdx]); return () => { mountedRef.current = false; }; }, [pageIdx, statusFilter, mobileSearch]);

    const searchByInvoice = async () => {
        if (!invoiceSearch.trim()) { loadOrders(''); return; }
        setLoading(true);
        try {
            const res = await LabServices.getOrderByInvoice(invoiceSearch.trim());
            const order = Array.isArray(res) ? res[0] : (res?.order ?? res?.orders?.[0] ?? res);
            setOrders(order ? [order] : []);
        } catch { setOrders([]); toast.error('Invoice not found'); }
        finally { setLoading(false); }
    };

    const visibleOrders = priorityFilter ? orders.filter(o => extractOrderPriority(o) === priorityFilter) : orders;

    return (
        <div className="flex flex-col gap-4 max-w-[1600px] mx-auto w-full">
            {/* ── Page Header ─────────────────────────────────── */}
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                <div className="flex items-center gap-2.5">
                    <div className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0" style={{ background: 'var(--v2-primary-500)' }}>
                        <FlaskConical className="w-4 h-4 text-white" strokeWidth={2.5} />
                    </div>
                    <div>
                        <h1 className="text-[16px] font-bold text-[var(--v2-text-heading)] m-0 tracking-tight">Lab Orders</h1>
                        <p className="text-[11px] text-[var(--v2-text-muted)] m-0 font-medium">Manage and process laboratory requisitions</p>
                    </div>
                </div>
                <Button variant="primary" size="small" onClick={() => setShowCreate(true)} leftIcon={<Plus className="w-full h-full" strokeWidth={2.5} />}>
                    New Order
                </Button>
            </div>

            {/* ── Toolbar ─────────────────────────────────── */}
            <div className="flex items-center gap-2.5 flex-wrap bg-[var(--v2-bg-card)] p-2.5 rounded-xl border border-[var(--v2-border-card)]">
                <div className="flex-1 min-w-[200px]">
                    <Input leftIcon={<Search className="w-full h-full" strokeWidth={2.5} />} placeholder="Search by Mobile…" size="small"
                        value={mobileSearch} onChange={e => { setMobileSearch(e.target.value); setPageIdx(0); }} />
                </div>
                <div className="flex-1 min-w-[200px]">
                    <Input leftIcon={<FileText className="w-full h-full" strokeWidth={2.5} />} placeholder="Search by Invoice no…" size="small"
                        value={invoiceSearch} onChange={e => setInvoiceSearch(e.target.value)}
                        onKeyDown={e => e.key === 'Enter' && searchByInvoice()} />
                </div>
                <FilterSelect
                    value={statusFilter}
                    onChange={v => { setStatusFilter(v); setPageIdx(0); }}
                    placeholder="All statuses"
                    options={[
                        { value: 'pending', label: 'Pending', dot: '#D97706' },
                        { value: 'processing', label: 'Processing', dot: '#7C3AED' },
                        { value: 'completed', label: 'Completed', dot: '#059669' },
                        { value: 'cancelled', label: 'Cancelled', dot: '#DC2626' },
                    ]}
                />
                <FilterSelect
                    value={priorityFilter}
                    onChange={v => { setPriorityFilter(v as OrderPriority | ''); setPageIdx(0); }}
                    placeholder="All priorities"
                    options={[
                        { value: 'stat', label: 'STAT', dot: PRIORITY_META.stat.color },
                        { value: 'urgent', label: 'Urgent', dot: PRIORITY_META.urgent.color },
                        { value: 'routine', label: 'Routine', dot: PRIORITY_META.routine.color },
                    ]}
                />
                {invoiceSearch && (
                    <Button variant="ghost" size="small" onClick={() => { setInvoiceSearch(''); loadOrders(''); }} className="!text-red-500 hover:!bg-red-50">
                        Clear
                    </Button>
                )}
            </div>

            {/* ── Table ───────────────────────────────────── */}
            {loading ? <OrderListSkeleton /> : (
                <div className="rounded-xl border border-[var(--v2-border-card)] bg-[var(--v2-bg-card)] overflow-hidden">
                    {visibleOrders.length === 0 ? (
                        <div className="flex flex-col items-center justify-center py-20 gap-3 text-center">
                            <div className="w-14 h-14 rounded-full bg-[var(--v2-bg-surface)] flex items-center justify-center mb-1">
                                <AlertCircle className="w-7 h-7 text-[var(--v2-text-muted)] opacity-50" strokeWidth={1.5} />
                            </div>
                            <p className="text-[15px] font-bold text-[var(--v2-text-heading)] m-0">No orders found</p>
                            <p className="text-[13px] text-[var(--v2-text-muted)] m-0 max-w-sm">Try adjusting your filters, searching for a different mobile number, or clear your current search criteria.</p>
                        </div>
                    ) : (
                        <div className="overflow-x-auto v2-scrollbar">
                            <table className="w-full text-left border-collapse">
                                <thead>
                                    <tr className="border-b border-[var(--v2-border-subtle)]" style={{ background: 'var(--v2-bg-surface)' }}>
                                        {['Patient Details', 'Tests Requested', 'Appointment', 'Order Status', 'Sample Status', ''].map((h, i) => (
                                            <th key={i} className="px-4 py-2.5 font-bold text-[var(--v2-text-secondary)] text-[10px] uppercase tracking-wider whitespace-nowrap">{h}</th>
                                        ))}
                                    </tr>
                                </thead>
                                <tbody>
                                    {visibleOrders.map(order => {
                                        const id = extractLabOrderId(order);
                                        const status = extractOrderStatus(order);
                                        const statusMeta = ORDER_STATUS_META[status];
                                        const slideStatus = resolveLabStatus(order);
                                        const slideMeta = SLIDE_LIBRARY_STATUS_META[slideStatus];
                                        const patientName = extractPatientName(order);
                                        const displayPatientName = formatNameTitleCase(patientName);
                                        const avatarColor = pickLabColor(id);
                                        const initials = getInitials(displayPatientName);
                                        const mrn = order.mrn;
                                        const tests = order.tests ?? [];
                                        const apptDate = extractAppointmentDate(order);
                                        const rawPatientId = order.patientId ?? '';
                                        const patientId = rawPatientId.startsWith('PATIENT#') ? rawPatientId.replace('PATIENT#', '') : rawPatientId;
                                        return (
                                            <tr key={id}
                                                className="group/row border-b border-[var(--v2-border-divider)] hover:bg-[var(--v2-bg-hover)] transition-colors cursor-pointer"
                                                onClick={() => openOrder(order)}>

                                                <td className="px-4 py-2.5">
                                                    <Tooltip
                                                        placement="right"
                                                        trigger="hover"
                                                        disabled={!patientId}
                                                        content={
                                                            <div className="flex flex-col gap-1 min-w-[160px]">
                                                                {mrn && <span className="font-bold">MRN {mrn}</span>}
                                                                {(order.age || order.gender) && (
                                                                    <span>{[order.age && `${order.age}Y`, order.gender].filter(Boolean).join(' · ')}</span>
                                                                )}
                                                                {order.mobile && <span>{order.mobile}</span>}
                                                                <span className="opacity-70 mt-0.5">Click to view profile</span>
                                                            </div>
                                                        }
                                                    >
                                                        <div
                                                            className={`flex items-center gap-2.5 ${patientId ? 'cursor-pointer' : ''}`}
                                                            onClick={(e) => {
                                                                if (!patientId) return;
                                                                e.stopPropagation();
                                                                navigate(`/patients-history/${patientId}`);
                                                            }}
                                                        >
                                                            <div className="w-7 h-7 rounded-full flex items-center justify-center text-[10px] font-bold text-white flex-shrink-0" style={{ background: avatarColor }}>
                                                                {initials}
                                                            </div>
                                                            <div className="min-w-0">
                                                                <p className={`font-bold m-0 whitespace-nowrap text-[13px] truncate max-w-[200px] ${patientId ? 'text-[var(--v2-primary-600,#045eb8)] hover:underline' : 'text-[var(--v2-text-primary)]'}`}>{displayPatientName}</p>
                                                                <div className="flex items-center gap-2 mt-0.5">
                                                                    {mrn && <span className="bg-[var(--v2-bg-surface)] text-[var(--v2-text-secondary)] text-[9px] font-bold px-1.5 py-0.5 rounded border border-[var(--v2-border-subtle)]">MRN: {mrn}</span>}
                                                                    <span className="text-[var(--v2-text-muted)] text-[10px] font-mono">ID: {id.replace(/^ORDER#/, '').slice(0, 8).toUpperCase()}</span>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </Tooltip>
                                                </td>

                                                <td className="px-4 py-2.5">
                                                    {tests.length === 0 ? (
                                                        <span className="text-[var(--v2-text-muted)] text-[12px] font-medium">—</span>
                                                    ) : (
                                                        <div className="flex flex-wrap items-center gap-1.5 max-w-[220px]">
                                                            <span className="inline-flex items-center gap-1.5 text-[11px] font-semibold px-2 py-0.5 rounded-md truncate max-w-[180px] border border-black/[0.05] dark:border-white/[0.07] group-hover/row:border-black/[0.08] dark:group-hover/row:border-white/[0.12] transition-colors"
                                                                style={{ background: 'rgba(128,128,128,0.05)', color: 'var(--v2-text-primary)' }} title={tests[0].testName}>
                                                                <FlaskConical className="w-3 h-3 text-[var(--v2-text-muted)] flex-shrink-0" strokeWidth={2.5} />
                                                                <span className="truncate">{tests[0].testName}</span>
                                                            </span>
                                                            {tests.length > 1 && (
                                                                <span className="relative inline-block group/moretests" onClick={(e) => e.stopPropagation()}>
                                                                    <span className="text-[10px] font-bold px-1.5 py-0.5 rounded-md flex-shrink-0 cursor-default"
                                                                        style={{ background: 'var(--v2-primary-50, rgba(4,94,184,0.08))', color: 'var(--v2-primary-600, #045eb8)' }}>
                                                                        +{tests.length - 1}
                                                                    </span>
                                                                    <div className="pointer-events-none absolute bottom-full left-1/2 -translate-x-1/2 mb-2 z-50 opacity-0 invisible group-hover/moretests:opacity-100 group-hover/moretests:visible transition-opacity duration-150">
                                                                        <div className="rounded-lg border border-[var(--v2-border-card)] p-2 max-w-[240px] shadow-lg"
                                                                            style={{ background: 'var(--v2-bg-card)' }}>
                                                                            <div className="flex flex-wrap gap-1.5">
                                                                                {tests.slice(1).map((t, idx) => (
                                                                                    <span key={idx} className="inline-flex items-center gap-1.5 text-[11px] font-semibold px-2 py-0.5 rounded-md whitespace-nowrap border border-[var(--v2-border-card)]"
                                                                                        style={{ background: 'var(--v2-bg-surface)', color: 'var(--v2-text-primary)' }}>
                                                                                        <FlaskConical className="w-3 h-3 text-[var(--v2-text-muted)] flex-shrink-0" strokeWidth={2.5} />
                                                                                        {t.testName}
                                                                                    </span>
                                                                                ))}
                                                                            </div>
                                                                        </div>
                                                                        <div className="w-2 h-2 rotate-45 mx-auto -mt-1" style={{ background: 'var(--v2-bg-card)', borderRight: '1px solid var(--v2-border-card)', borderBottom: '1px solid var(--v2-border-card)' }} />
                                                                    </div>
                                                                </span>
                                                            )}
                                                        </div>
                                                    )}
                                                </td>

                                                <td className="px-4 py-2.5 text-[11px] font-semibold text-[var(--v2-text-secondary)] whitespace-nowrap">
                                                    {apptDate !== '—' ? (
                                                        <div className="flex items-center gap-1.5">
                                                            <div className="w-1.5 h-1.5 rounded-full bg-[#7C3AED]" />
                                                            {apptDate}
                                                        </div>
                                                    ) : <span className="opacity-50">—</span>}
                                                </td>

                                                <td className="px-4 py-2.5">
                                                    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold whitespace-nowrap"
                                                        style={{ background: statusMeta.bg, color: statusMeta.color, border: `1px solid ${statusMeta.color}30` }}>
                                                        {statusMeta.label}
                                                    </span>
                                                </td>

                                                <td className="px-4 py-2.5">
                                                    {slideMeta ? (
                                                        (slideStatus === 'REPORT_READY' || slideStatus === 'COMPLETED') ? (
                                                            <button type="button" onClick={e => handleViewReport(order, e)} disabled={viewingReportId === id}
                                                                title="View generated report"
                                                                className="inline-flex items-center gap-1.5 text-[10px] font-bold px-2 py-0.5 rounded-full whitespace-nowrap cursor-pointer border-0 disabled:opacity-60 hover:brightness-95 transition-[filter]"
                                                                style={{ background: slideMeta.bg, color: slideMeta.color, border: `1px solid ${slideMeta.color}30` }}>
                                                                <span className="w-1.5 h-1.5 rounded-full flex-shrink-0" style={{ background: slideMeta.dot }} />
                                                                {viewingReportId === id ? 'Opening…' : slideMeta.label}
                                                            </button>
                                                        ) : (
                                                            <span className="inline-flex items-center gap-1.5 text-[10px] font-bold px-2 py-0.5 rounded-full whitespace-nowrap"
                                                                style={{ background: slideMeta.bg, color: slideMeta.color, border: `1px solid ${slideMeta.color}30` }}>
                                                                <span className="w-1.5 h-1.5 rounded-full flex-shrink-0" style={{ background: slideMeta.dot }} />
                                                                {slideMeta.label}
                                                            </span>
                                                        )
                                                    ) : (
                                                        <span className="text-[var(--v2-text-muted)] text-[11px] font-medium">—</span>
                                                    )}
                                                </td>

                                                <td className="px-4 py-2.5 text-right" onClick={e => e.stopPropagation()}>
                                                    <button type="button" onClick={() => openOrder(order)}
                                                        className="w-7 h-7 rounded-lg flex items-center justify-center cursor-pointer border-0 text-[var(--v2-text-muted)] hover:text-[var(--v2-primary-600,#045eb8)] hover:bg-[var(--v2-bg-hover)] bg-transparent transition-colors" title="View full details">
                                                        <ArrowRight className="w-3.5 h-3.5" strokeWidth={2.5} />
                                                    </button>
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>
            )}

            {/* ── Pagination ──────────────────────────────── */}
            {!loading && orders.length > 0 && (
                <div className="flex items-center justify-between px-1">
                    <span className="text-[12px] font-semibold text-[var(--v2-text-muted)]">
                        Page {pageIdx + 1} <span className="opacity-50 mx-1">·</span> <span className="text-[var(--v2-text-primary)]">{visibleOrders.length}</span> orders
                    </span>
                    <div className="flex gap-2">
                        <Button variant="outline" size="small" disabled={pageIdx === 0} onClick={() => setPageIdx(p => Math.max(0, p - 1))}
                            leftIcon={<ChevronLeft className="w-full h-full" strokeWidth={2.5} />}>
                            Prev
                        </Button>
                        <Button variant="outline" size="small" disabled={pageIdx >= tokenStack.length - 1} onClick={() => setPageIdx(p => p + 1)}
                            rightIcon={<ChevronRight className="w-full h-full" strokeWidth={2.5} />}>
                            Next
                        </Button>
                    </div>
                </div>
            )}

            {showCreate && <PlaceOrderModal onClose={() => setShowCreate(false)} onSuccess={() => { loadOrders(''); setPageIdx(0); }} />}
        </div>
    );
};

export default OrderManagement;
